/**
 * <LivePreviewPanel /> — embeds the storefront homepage in an iframe so the
 * admin sees their CMS edits applied in real time, side-by-side with the
 * editor. Subscribes to `usePreviewStore.version` and reloads the iframe (by
 * bumping a cache-busting query param on `src`) whenever any homepage-related
 * mutation calls `usePreviewStore.getState().bump()`.
 *
 * Device toggles let the admin preview at desktop / tablet / mobile widths
 * without resizing their browser. The iframe scales the storefront page down
 * with a CSS transform so the full 1280px page is visible inside the panel.
 *
 * Storefront URL defaults to `http://localhost:5173` in dev. Override via
 * `VITE_STOREFRONT_URL` env var when deploying behind a different domain
 * (resolved in `lib/appUrls.ts`).
 */
import { ExternalLink, Monitor, RefreshCw, Smartphone, Tablet } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { STOREFRONT_URL } from '../lib/appUrls';
import { usePreviewStore } from '../stores/previewStore';

type Device = 'desktop' | 'tablet' | 'mobile';

/** Logical width that we render the storefront at (CSS pixels of the page). */
const DEVICE_WIDTH: Record<Device, number> = {
  desktop: 1280,
  tablet: 768,
  mobile: 414,
};

/** Optional config so each page can tailor the panel (eg. start in mobile preview). */
interface LivePreviewPanelProps {
  /** Defaults to 'desktop'. */
  defaultDevice?: Device;
  /** Optional `?path=` to load instead of `/` (e.g. preview a specific route). */
  path?: string;
  /** Optional className extension for the outer wrapper. */
  className?: string;
}

export function LivePreviewPanel({
  defaultDevice = 'desktop',
  path = '/',
  className = '',
}: LivePreviewPanelProps) {
  const version = usePreviewStore((s) => s.version);
  const bumpVersion = usePreviewStore((s) => s.bump);
  const [device, setDevice] = useState<Device>(defaultDevice);
  // localVersion lets us trigger a manual refresh without touching the global store.
  const [localBump, setLocalBump] = useState(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Cache-busting param forces the iframe to fully reload whenever either
  // the global version (admin saved something) or localBump (refresh button)
  // changes. Same-origin would let us call location.reload(); cross-origin
  // (admin is on a different port) requires touching `src`.
  const iframeSrc = useMemo(() => {
    const sep = path.includes('?') ? '&' : '?';
    return `${STOREFRONT_URL}${path}${sep}_pv=${version}-${localBump}`;
  }, [path, version, localBump]);

  // Scale the storefront page down so the chosen device width fits inside a
  // panel that's typically ~480px wide. We measure the panel and compute a
  // scale ratio; the iframe is rendered at the logical device width and
  // then `transform: scale(ratio)` shrinks it.
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(480);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setContainerWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const deviceWidth = DEVICE_WIDTH[device];
  // 20px of horizontal breathing room inside the device frame.
  const scale = Math.min(1, (containerWidth - 24) / deviceWidth);
  const scaledHeight = 720 * scale; // ~720px viewport height, scaled

  return (
    <div
      className={`border-border/60 bg-surface sticky top-6 flex flex-col overflow-hidden rounded-2xl border shadow-sm ${className}`}
    >
      {/* Header: title + device toggles + actions */}
      <div className="border-border/60 bg-surface-subtle/30 flex items-center justify-between gap-2 border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="bg-brand-primary/10 text-brand-primary grid h-7 w-7 place-items-center rounded-lg">
            <Monitor className="h-3.5 w-3.5" />
          </span>
          <div>
            <h3 className="text-ink text-[13px] font-bold leading-tight">Live Preview</h3>
            <p className="text-ink-muted text-[10px] font-medium leading-tight">
              Auto-refreshes after every save
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1">
          {/* Device toggle pills */}
          <div className="bg-surface-subtle/50 border-border/60 flex items-center rounded-lg border p-0.5">
            <DeviceButton
              active={device === 'desktop'}
              onClick={() => setDevice('desktop')}
              icon={<Monitor className="h-3.5 w-3.5" />}
              label="Desktop"
            />
            <DeviceButton
              active={device === 'tablet'}
              onClick={() => setDevice('tablet')}
              icon={<Tablet className="h-3.5 w-3.5" />}
              label="Tablet"
            />
            <DeviceButton
              active={device === 'mobile'}
              onClick={() => setDevice('mobile')}
              icon={<Smartphone className="h-3.5 w-3.5" />}
              label="Mobile"
            />
          </div>

          {/* Manual refresh */}
          <button
            type="button"
            onClick={() => setLocalBump((b) => b + 1)}
            className="text-ink-muted hover:bg-surface-subtle hover:text-ink grid h-7 w-7 place-items-center rounded-lg transition-colors"
            aria-label="Refresh preview"
            title="Refresh preview"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>

          {/* Open in new tab */}
          <a
            href={`${STOREFRONT_URL}${path}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-ink-muted hover:bg-surface-subtle hover:text-ink grid h-7 w-7 place-items-center rounded-lg transition-colors"
            aria-label="Open in new tab"
            title="Open storefront in new tab"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>

      {/* Iframe stage */}
      <div
        ref={containerRef}
        className="bg-surface-subtle/40 flex flex-1 items-start justify-center p-3"
        style={{ minHeight: `${scaledHeight + 24}px` }}
      >
        <div
          className="border-border/60 origin-top overflow-hidden rounded-xl border bg-white shadow-md"
          style={{
            width: `${deviceWidth}px`,
            height: `720px`,
            transform: `scale(${scale})`,
          }}
        >
          <iframe
            ref={iframeRef}
            key={iframeSrc} // force remount on cache-buster change (cross-origin safe)
            src={iframeSrc}
            title="Storefront preview"
            className="h-full w-full border-0"
            // sandbox left off so the storefront can run scripts / fonts normally.
          />
        </div>
      </div>

      {/* Footer hint */}
      <div className="border-border/60 bg-surface-subtle/20 flex items-center justify-between border-t px-4 py-2">
        <span className="text-ink-muted text-[10px] font-medium">
          {deviceWidth}px @ {Math.round(scale * 100)}%
        </span>
        <button
          type="button"
          onClick={() => bumpVersion()}
          className="text-ink-muted hover:text-brand-primary text-[10px] font-bold transition-colors"
          title="Trigger reload across any other preview panels"
        >
          Force refresh
        </button>
      </div>
    </div>
  );
}

interface DeviceButtonProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}

function DeviceButton({ active, onClick, icon, label }: DeviceButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={`grid h-6 w-7 place-items-center rounded-md transition-all ${
        active
          ? 'bg-brand-primary text-white shadow-sm'
          : 'text-ink-muted hover:bg-surface hover:text-ink'
      }`}
    >
      {icon}
    </button>
  );
}
